import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/**
 * Phone/Web-side pairing page.
 * User visits /tv-pair?code=XXXXXX (from QR scan) while logged in.
 * Sends their token to /api/tv/pair so the TV session becomes authenticated.
 */
const TVPairPhone = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const [code, setCode] = useState((params.get('code') || '').toUpperCase());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!code || code.length !== 6) { setError('Enter the 6-character code from your TV.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const res = await api.post('/tv/pair', { code: code.toUpperCase(), token });
      setResult(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not pair. Try a fresh code.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // If we arrived from a QR scan with a code and the user is logged in, auto-submit
    if (!loading && isAuthenticated && (params.get('code') || '').length === 6 && !result && !submitting) {
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthenticated]);

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center' }}>Loading…</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="tv-phone-pair" style={{ background: '#fff', minHeight: '100vh' }} data-testid="tv-pair-phone-signin">
        <h1>Sign in to pair your TV</h1>
        <p style={{ marginTop: 12, color: '#475569' }}>You need to sign in to Pulse Media before linking your TV. We saved your code — keep this page open.</p>
        {code && <div style={{ marginTop: 12, fontFamily: 'monospace', fontSize: 20 }}>Code: <strong>{code}</strong></div>}
        <button
          onClick={() => navigate(`/signin?next=/tv-pair%3Fcode%3D${encodeURIComponent(code)}`)}
          data-testid="tv-pair-go-signin-btn"
        >Sign in</button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="tv-phone-pair" style={{ background: '#fff', minHeight: '100vh' }} data-testid="tv-pair-phone-success">
        <h1 style={{ color: '#059669' }}>✓ TV Paired</h1>
        <p style={{ marginTop: 12, color: '#475569' }}>
          Your TV is now signed in as <strong>{user?.username || user?.email}</strong>. You can close this page.
        </p>
        <button onClick={() => navigate('/')}>Back to Pulse Media</button>
      </div>
    );
  }

  return (
    <div className="tv-phone-pair" style={{ background: '#fff', minHeight: '100vh' }} data-testid="tv-pair-phone">
      <h1>Link your TV</h1>
      <p style={{ marginTop: 12, color: '#475569' }}>
        Enter the 6-character code shown on your TV to sign in as <strong>{user?.username || user?.email}</strong>.
      </p>
      <form onSubmit={submit}>
        <input
          className="code-inp"
          value={code}
          maxLength={6}
          placeholder="ABC123"
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          data-testid="tv-pair-code-input"
        />
        {error && <div style={{ color: '#b91c1c', marginTop: 12, fontWeight: 600 }} data-testid="tv-pair-error">{error}</div>}
        <button type="submit" disabled={submitting || code.length !== 6} data-testid="tv-pair-submit-btn">
          {submitting ? 'Linking…' : 'Link TV'}
        </button>
      </form>
    </div>
  );
};

export default TVPairPhone;
